import type { Template, TemplateDraft } from '@/types/templateTypes';
import { Client } from '@langchain/langgraph-sdk';
import type {
  GetTokenWithPopupOptions,
  PopupConfigOptions
} from '@auth0/auth0-react';

type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;
type GetAccessTokenWithPopupType = (
  options?: GetTokenWithPopupOptions,
  config?: PopupConfigOptions
) => Promise<string | undefined>;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const LANGGRAPH_BASE_URL =
  (import.meta as any).env.VITE_LANGGRAPH_BASE_URL || 'http://localhost:2024';
const UPLOAD_ENDPOINT = `${API_BASE_URL}/templates/import/upload`;
const PROCESS_ENDPOINT = `${API_BASE_URL}/templates/import/process`;
const JOB_START_ENDPOINT = `${API_BASE_URL}/template-imports`;

const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    if (
      error?.error === 'consent_required' ||
      error?.error === 'login_required' ||
      error?.error === 'interaction_required'
    ) {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token)
        throw new Error('Failed to acquire token for template import');
      return token;
    }
    throw error;
  }
};

export type UploadResult = {
  companyDocumentId: string;
  documentUrl: string;
  fileName: string;
  contentType: string;
};

const createLangGraphClient = (apiUrl: string, token: string, userId: string) =>
  new Client({
    apiUrl,
    defaultHeaders: { Authorization: `Bearer ${token}`, 'X-User-Id': userId }
  });

export const uploadTemplateSource = async (
  file: File,
  templateCategoryId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  templateName?: string
): Promise<UploadResult> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const form = new FormData();
  form.append('file', file);
  form.append('templateCategoryId', String(templateCategoryId));
  if (templateName) form.append('templateName', templateName);

  const resp = await fetch(UPLOAD_ENDPOINT, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'X-User-Id': userId },
    body: form
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ message: resp.statusText }));
    throw new Error(err.message || 'Upload failed');
  }
  return (await resp.json()) as UploadResult;
};

const parseWithLangGraph = async (
  documentUrl: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  maxQuestions = 200
): Promise<TemplateDraft> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const client = createLangGraphClient(LANGGRAPH_BASE_URL, accessToken, userId);
  const thread = await client.threads.create();
  const threadId = thread.thread_id;
  const result = await client.runs.wait(
    threadId,
    'import_template_from_document_graph',
    {
      input: {
        blob_url: documentUrl,
        max_questions: maxQuestions
      },
      config: {
        configurable: {
          user_id: userId,
          thread_id: threadId,
          blob_url: documentUrl,
          max_questions: maxQuestions
        }
      },
      metadata: {
        user_id: userId,
        thread_id: threadId,
        blob_url: documentUrl,
        max_questions: maxQuestions
      }
    }
  );

  // Parse template from message content
  const messages = (result as any)?.messages || [];
  if (!messages.length) {
    throw new Error('No messages returned from template import');
  }

  const lastMessage = messages[messages.length - 1];
  const content = lastMessage?.content || '';

  // Check for error
  if (content.startsWith('ERROR:')) {
    throw new Error(content.replace('ERROR: ', ''));
  }

  // Parse JSON from message content
  try {
    const parsed = JSON.parse(content);
    return parsed as TemplateDraft;
  } catch (e) {
    throw new Error('Failed to parse template from response: ' + content);
  }
};

export const createTemplateFromDraft = async (
  upload: UploadResult,
  draft: TemplateDraft,
  templateCategoryId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  templateName?: string
): Promise<Template> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const resp = await fetch(PROCESS_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify({
      companyDocumentId: upload.companyDocumentId,
      templateCategoryId,
      templateName,
      parsedTemplate: draft
    })
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ message: resp.statusText }));
    throw new Error(err.message || 'Create from draft failed');
  }
  return (await resp.json()) as Template;
};

// High-level: Upload → LangGraph parse → Persist
export const importTemplate = async (
  file: File,
  templateCategoryId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  templateName?: string
): Promise<Template> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const form = new FormData();
  form.append('file', file);
  form.append('templateCategoryId', String(templateCategoryId));
  if (templateName) form.append('templateName', templateName);

  const startResp = await fetch(JOB_START_ENDPOINT, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'X-User-Id': userId },
    body: form
  });
  if (!(startResp.status === 202 || startResp.status === 200)) {
    const err = await startResp
      .json()
      .catch(() => ({ message: startResp.statusText }));
    throw new Error(err.message || 'Failed to start template import');
  }
  const { jobId } = await startResp.json();

  // Wait for completion by polling (UI component will prefer SSE). This preserves existing service API.
  const statusUrl = `${JOB_START_ENDPOINT}/${jobId}`;
  const poll = async (): Promise<Template> => {
    while (true) {
      const resp = await fetch(statusUrl, {
        headers: { Authorization: `Bearer ${accessToken}`, 'X-User-Id': userId }
      });
      if (!resp.ok) throw new Error('Failed to get job status');
      const s = await resp.json();
      if (s.status === 'Completed' || s.status === 5) {
        if (!s.templateId) throw new Error('Job completed without templateId');
        const tplResp = await fetch(
          `${API_BASE_URL}/templates/${s.templateId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'X-User-Id': userId
            }
          }
        );
        if (!tplResp.ok) throw new Error('Failed to load created template');
        return (await tplResp.json()) as Template;
      }
      if (s.status === 'Failed' || s.status === 6) {
        throw new Error(s.message || 'Template import failed');
      }
      await new Promise((r) => setTimeout(r, 1000));
    }
  };
  return await poll();
};

export const startTemplateImportJob = async (
  file: File,
  templateCategoryId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  templateName?: string
): Promise<{ jobId: string; upload: UploadResult }> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const form = new FormData();
  form.append('file', file);
  form.append('templateCategoryId', String(templateCategoryId));
  if (templateName) form.append('templateName', templateName);
  const startResp = await fetch(JOB_START_ENDPOINT, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'X-User-Id': userId },
    body: form
  });
  if (!(startResp.status === 202 || startResp.status === 200)) {
    const err = await startResp
      .json()
      .catch(() => ({ message: startResp.statusText }));
    throw new Error(err.message || 'Failed to start template import');
  }
  return (await startResp.json()) as { jobId: string; upload: UploadResult };
};

export type TemplateImportStatus =
  | 'Queued'
  | 'Uploading'
  | 'Parsing'
  | 'Creating'
  | 'Finalizing'
  | 'Completed'
  | 'Failed';

export type TemplateImportUpdate = {
  status: TemplateImportStatus | number;
  message: string;
  percent: number;
  templateId?: number;
};

export const getTemplateImportStatus = async (
  jobId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TemplateImportUpdate> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const resp = await fetch(`${JOB_START_ENDPOINT}/${jobId}`, {
    headers: { Authorization: `Bearer ${accessToken}`, 'X-User-Id': userId }
  });
  if (!resp.ok) throw new Error('Failed to get import status');
  return (await resp.json()) as TemplateImportUpdate;
};

export const getTemplateById = async (
  id: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Template> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const resp = await fetch(`${API_BASE_URL}/templates/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}`, 'X-User-Id': userId }
  });
  if (!resp.ok) throw new Error('Failed to fetch template');
  return (await resp.json()) as Template;
};

export const parseUploadedDocument = async (
  documentUrl: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  maxQuestions = 200
): Promise<TemplateDraft> => {
  return await parseWithLangGraph(
    documentUrl,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    maxQuestions
  );
};

export const processTemplateImportJob = async (
  jobId: string,
  req: {
    companyDocumentId: string;
    templateCategoryId: number;
    templateName?: string;
    parsedTemplate: TemplateDraft;
  },
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Template> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const resp = await fetch(`${JOB_START_ENDPOINT}/${jobId}/process`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify(req)
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ message: resp.statusText }));
    throw new Error(err.message || 'Failed to process template import job');
  }
  return (await resp.json()) as Template;
};

export const updateTemplateImportStatus = async (
  jobId: string,
  status: TemplateImportStatus,
  percent: number,
  message: string,
  templateId: number | undefined,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<void> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  await fetch(`${JOB_START_ENDPOINT}/${jobId}/status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify({ status, percent, message, templateId })
  });
};

export const previewImportFromFile = async (
  file: File,
  templateCategoryId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  templateName?: string
): Promise<TemplateDraft> => {
  const upload = await uploadTemplateSource(
    file,
    templateCategoryId,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    templateName
  );
  return await parseWithLangGraph(
    upload.documentUrl,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
};

export const importFromFile = async (
  file: File,
  templateCategoryId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  templateName?: string
): Promise<Template> => {
  return await importTemplate(
    file,
    templateCategoryId,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    templateName
  );
};
